/**
 * BctPage — Backchannel Tree (prototype v38 `#page-bct`).
 *
 * Flow:
 *  1. User picks an import from the dropdown and fills the form, clicks "Generate Tree".
 *  2. The API classifies the in-scope job titles into the "Roles to track"
 *     categories with Claude, and results appear grouped by category
 *     (preview only — no DB writes).
 *  3. Download CSV button (always visible, enabled once results exist) saves locally.
 *  4. User clicks "Save" → bct_run + child import persisted, navigates to ImportsPage.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMe } from "@nu-assist/shared-fe";
import { Button, PageHeading, Spinner } from "@nu-assist/ui";
import { ImportSelectionBar } from "../../features/imports/selection/ImportSelectionBar";
import { useSelectedImport } from "../../features/imports/selection/SelectedImportContext";
import { bctUnusable } from "../../features/imports/selection/requirements";
import type { ImportSummary } from "@nu-assist/contracts";
import {
  createBctClient,
  type BctClient,
  type BctCompany,
  type BctPreviewResponse,
  type BctResultRow,
  type BctRunResponse,
  type BctRunRequest,
  type CompanySuggestion,
} from "../../clients/bct.client";
import { CompanyTypeahead } from "./CompanyTypeahead";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { usePromptViewerAllowed } from "../../features/ai-review-prompt/access";
import { CompanyMapPromptModal } from "../../features/company-map-prompt/CompanyMapPromptModal";
import { createImportsClient, type ImportsClient } from "../../clients/imports.client";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const DEFAULT_CLIENT = createBctClient();
const DEFAULT_IMPORTS_CLIENT = createImportsClient({ apiBase: API_BASE });

// ── Form state ───────────────────────────────────────────────────────────────

interface CompanyEntry {
  _key: string;
  name: string;
  id: string;
  /** True once a typeahead suggestion was picked (collapses the input to a
   * summary + "Change"). Free-text entries stay `false` and submit `id: null`. */
  selected: boolean;
  startYear: string;
  endYear: string;
  /** When true, end_year is sent as null (API treats null as "present"). */
  presentYear: boolean;
}

interface FormState {
  companies: CompanyEntry[];
  /** One "Roles to track" text for the whole run, as in Jared's Sheets
   *  add-on: a description or a comma-separated list. Blank → the default. */
  rolesDescription: string;
}

/** The add-on's default, shown so an empty box is not a mystery. The API
 *  owns the value (`DEFAULT_ROLES_DESCRIPTION` in python-shared); this copy
 *  is display text only. */
const DEFAULT_ROLES_HINT = "All C-level, VP-level, board members, board directors, board chairman";

// crypto.randomUUID() is Web Crypto API — available in all modern browsers
// and Node 19+. No polyfill or import required.
function makeEmptyCompany(): CompanyEntry {
  return {
    _key: crypto.randomUUID(),
    name: "",
    id: "",
    selected: false,
    startYear: "",
    endYear: "",
    presentYear: true,
  };
}

function makeEmptyForm(): FormState {
  return { companies: [makeEmptyCompany()], rolesDescription: "" };
}

function formToRequest(form: FormState): BctRunRequest {
  return {
    companies: form.companies
      .filter((c) => c.name.trim() !== "")
      .map(
        (c): BctCompany => ({
          name: c.name.trim(),
          id: c.id.trim() || null,
          start_year: c.startYear ? Number(c.startYear) : null,
          end_year: c.presentYear ? null : c.endYear ? Number(c.endYear) : null,
        })
      ),
    roles_description: form.rolesDescription.trim() || null,
  };
}

/** Same scope and same roles text — the preview's classification still
 *  answers this request, so the save can reference it. Compared on the request
 *  body, which is exactly what the API would classify. */
function sameRequest(a: BctRunRequest, b: BctRunRequest): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ── Page states ──────────────────────────────────────────────────────────────

/** A preview plus the request that produced it — the save references the
 *  preview's classification only while the form still says the same thing. */
interface PreviewResult {
  response: BctPreviewResponse;
  request: BctRunRequest;
}

type PageState =
  | { phase: "idle" }
  | { phase: "previewing" }
  | { phase: "preview_ready"; preview: PreviewResult }
  | { phase: "saving"; preview: PreviewResult }
  | { phase: "polling"; runId: string; preview: PreviewResult };

// ── CSV helpers ──────────────────────────────────────────────────────────────

/**
 * THIRD copy of this column list. The other two are `BCT_CSV_FIELDNAMES` in
 * `api/app/web/v2/bct/domain.py` (which builds the rows) and `BctResultRow` in
 * `clients/bct.client.ts` (which types them). Nothing checks that the three
 * agree, because the preview response schema types `rows` as a bare
 * `dict[str, str]` and codegen therefore sees no keys. Adding a column means
 * editing all three; `Array<keyof BctResultRow>` at least makes the compiler
 * catch a typo here.
 */
const BCT_CSV_FIELDNAMES: Array<keyof BctResultRow> = [
  "First Name",
  "Last Name",
  "Title",
  "Company",
  "Start",
  "End",
  "Degree",
  "Category",
  "LinkedIn URL",
];

const _FORMULA_PREFIXES = ["=", "+", "-", "@"];

export function rfc4180Cell(value: string): string {
  const safe = _FORMULA_PREFIXES.some((p) => value.startsWith(p)) ? `'${value}` : value;
  if (safe.includes('"') || safe.includes(",") || safe.includes("\n")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

export function buildCsvDataUrl(rows: BctResultRow[]): string {
  const header = BCT_CSV_FIELDNAMES.join(",");
  const lines = rows.map((row) =>
    BCT_CSV_FIELDNAMES.map((col) => rfc4180Cell(row[col] ?? "")).join(",")
  );
  const csv = [header, ...lines].join("\n");
  return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
}

// ── Companies display label ──────────────────────────────────────────────────

function companiesLabel(companies: BctCompany[]): string {
  if (companies.length === 0) return "—";
  if (companies.length === 1) return companies[0].name;
  return `${companies.length} companies`;
}

// ── Category group helpers ───────────────────────────────────────────────────

/**
 * Rows grouped by their AI category, in the order the classification returned
 * the categories — the order the add-on's sheet used. A category with no rows
 * is not shown. A row whose category is not in the list (the API guards a
 * formula-looking category with a leading quote, which changes the string)
 * still renders, in a group after the known ones, rather than vanishing.
 */
function groupByCategory(
  rows: BctResultRow[],
  categories: readonly string[]
): Array<{ category: string; rows: BctResultRow[] }> {
  const grouped = new Map<string, BctResultRow[]>();
  for (const row of rows) {
    const category = row.Category ?? "";
    if (!grouped.has(category)) grouped.set(category, []);
    grouped.get(category)?.push(row);
  }
  const known = categories.filter((c) => grouped.has(c));
  const rest = [...grouped.keys()].filter((c) => !categories.includes(c));
  return [...known, ...rest].map((category) => ({
    category,
    rows: grouped.get(category) ?? [],
  }));
}

/**
 * What an empty map means, said truthfully. Two different facts produce zero
 * rows now that a model decides who stays: nobody worked there in that window
 * (no titles reached the classifier), or people did and none of their titles
 * is one of the roles to track.
 */
function emptyMapMessage(preview: BctPreviewResponse): string {
  const where = companiesLabel(preview.companies);
  // Tolerates a response without a classification: a web deploy can land
  // before the API's (PR #982 review, C M-9).
  const titles = Object.keys(preview.classification?.mapping ?? {}).length;
  if (titles === 0) return `No one in this people list worked at ${where} in that date range.`;
  return `${titles} job title${titles !== 1 ? "s" : ""} at ${where}, none of them a role to track: “${preview.roles_description}”.`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

const INPUT_CLASS =
  "rounded-sm border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50";

const SELECT_CLASS =
  "rounded-sm border border-border bg-surface px-2 py-1.5 text-body text-text focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50";

function CompanyRow({
  entry,
  index,
  importId,
  onChange,
  onRemove,
  canRemove,
  searchCompanies,
}: {
  entry: CompanyEntry;
  index: number;
  importId: string;
  onChange: (patch: Omit<Partial<CompanyEntry>, "_key">) => void;
  onRemove: () => void;
  canRemove: boolean;
  searchCompanies: (importId: string, q: string, limit?: number) => Promise<CompanySuggestion[]>;
}) {
  const nameId = `bct-company-name-${index}`;
  const companyIdId = `bct-company-id-${index}`;
  return (
    <div className="rounded-sm border border-border bg-surface p-3 flex flex-col gap-2">
      {/* Row 1: Company name + ID + Remove */}
      <div className="flex gap-3.5 items-end">
        <div className="flex flex-1 min-w-0 flex-col gap-1">
          <label
            htmlFor={nameId}
            className="text-label font-semibold text-text-secondary uppercase tracking-wide"
          >
            Company
          </label>
          <CompanyTypeahead
            index={index}
            inputId={nameId}
            inputClassName={INPUT_CLASS}
            value={entry.name}
            selected={entry.selected}
            importId={importId || null}
            search={(q) => searchCompanies(importId, q, 10)}
            onTextChange={(name) =>
              // Free-text edit — never auto-clears an already-entered id, but a
              // hand-typed name is a non-suggestion so it stays `selected: false`
              // and submits with whatever id the user has (or null).
              onChange({ name, selected: false })
            }
            onSelect={(s) => onChange({ name: s.name, id: s.id, selected: true })}
            onClear={() => onChange({ name: "", id: "", selected: false })}
          />
        </div>
        <div className="flex flex-1 min-w-0 flex-col gap-1">
          <label
            htmlFor={companyIdId}
            className="text-label font-semibold text-text-secondary uppercase tracking-wide"
          >
            Company ID{" "}
            <span className="font-normal text-text-muted normal-case tracking-normal">
              (LinkedIn)
            </span>
          </label>
          <input
            id={companyIdId}
            className={INPUT_CLASS}
            placeholder={importId ? "auto-fills on pick" : "Select a people list first"}
            value={entry.id}
            disabled={!importId}
            readOnly
            // Reference-only: the ID is derived from picking a company in the
            // typeahead, never hand-typed. To change it, change the Company
            // field above — this input never fires onChange.
            data-testid={`bct-company-id-${index}`}
          />
        </div>
        <div className="flex-shrink-0 pb-px">
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-label text-error hover:underline"
              data-testid={`bct-remove-company-${index}`}
              aria-label="Remove company"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Year range (per-company). The role moved out of the row: it is
          one "Roles to track" text for the whole run, as in the add-on. */}
      <div className="flex flex-wrap items-end gap-3.5">
        <div className="flex flex-shrink-0 flex-col gap-1">
          <span className="text-label font-semibold text-text-secondary uppercase tracking-wide">
            Date Range
          </span>
          <div className="flex items-center gap-2">
            <select
              className={SELECT_CLASS}
              value={entry.startYear}
              data-testid={`bct-start-year-${index}`}
              onChange={(e) => onChange({ startYear: e.target.value })}
            >
              <option value="">Year</option>
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span className="text-body text-text-secondary">to</span>
            <select
              className={SELECT_CLASS}
              value={entry.endYear}
              disabled={entry.presentYear}
              data-testid={`bct-end-year-${index}`}
              onChange={(e) => onChange({ endYear: e.target.value })}
            >
              <option value="">Year</option>
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-body text-text">
              <input
                type="checkbox"
                checked={entry.presentYear}
                data-testid={`bct-present-year-${index}`}
                style={{ accentColor: "var(--accent)" }}
                onChange={(e) =>
                  onChange({
                    presentYear: e.target.checked,
                    endYear: e.target.checked ? "" : entry.endYear,
                  })
                }
              />
              Present
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

const CURRENT_YEAR = new Date().getFullYear();
// First year offered in the Date Range selects (Trello #837). The API still
// accepts 1900+, and the filter is overlap-based, so positions that started
// before this year still match.
const FIRST_YEAR = 2000;
const YEARS = Array.from({ length: CURRENT_YEAR - FIRST_YEAR + 1 }, (_, i) =>
  String(FIRST_YEAR + i)
);

function BctForm({
  form,
  onChange,
  onGenerate,
  generating,
  preview,
  searchCompanies,
  importReady,
  saving,
  importId,
}: {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  onGenerate: () => void;
  generating: boolean;
  preview: BctPreviewResponse | null;
  searchCompanies: (importId: string, q: string, limit?: number) => Promise<CompanySuggestion[]>;
  /** A usable import is selected (present AND accepted by `bctUnusable`). */
  importReady: boolean;
  /** A save is in flight (creating or polling). */
  saving: boolean;
  /** The app-wide selection's id — the company typeahead scopes to it. */
  importId: string | null;
}) {
  const hasValidCompany = form.companies.some((c) => c.name.trim() !== "");
  // `saving` is in the guard, not just `generating`: a Generate click during a
  // save bumps the generation token, which correctly silences the in-flight
  // poll — but the run is alive server-side and will persist a child import,
  // so the recruiter would be left on a preview with nothing said. Silence is
  // right when the SELECTION moved (the page is moving on); it is wrong here,
  // where they are still on the same import. `bct-save` was already gated.
  const canGenerate = importReady && hasValidCompany && !generating && !saving;
  const hasResults = preview !== null && preview.match_count > 0;

  function patchCompany(index: number, patch: Omit<Partial<CompanyEntry>, "_key">) {
    onChange({
      companies: form.companies.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  }

  function addCompany() {
    onChange({ companies: [...form.companies, makeEmptyCompany()] });
  }

  function removeCompany(index: number) {
    onChange({ companies: form.companies.filter((_, i) => i !== index) });
  }

  function handleDownloadCsv() {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = buildCsvDataUrl(preview.rows);
    a.download = `${companiesLabel(preview.companies)}-CompanyMap-preview.csv`;
    a.click();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canGenerate) onGenerate();
      }}
    >
      {/* Company Map scope — amber card */}
      <div className="mb-4 rounded-sm border border-warning-strong bg-warning-muted p-4">
        <div className="mb-1 text-label font-bold text-warning-ink">Company Map scope</div>
        <p className="mb-3 text-label text-text-secondary">
          Restrict the Company Map to contacts who worked at each company. Each entry carries its
          own year range. Names with the same label are disambiguated by ID.
        </p>

        <div className="mb-2 flex flex-col gap-3">
          {form.companies.map((entry, i) => (
            <CompanyRow
              key={entry._key}
              entry={entry}
              index={i}
              importId={importId ?? ""}
              onChange={(patch) => patchCompany(i, patch)}
              onRemove={() => removeCompany(i)}
              canRemove={form.companies.length > 1}
              searchCompanies={searchCompanies}
            />
          ))}
        </div>

        <button
          type="button"
          data-testid="bct-add-company"
          onClick={addCompany}
          className="mt-1 text-label text-accent hover:underline"
        >
          + Add company
        </button>
      </div>

      {/* Roles to track — one text for the whole run (Jared's add-on prompt) */}
      <div className="mb-4 flex flex-col gap-1">
        <label
          htmlFor="bct-roles-description"
          className="text-label font-semibold text-text-secondary uppercase tracking-wide"
        >
          Roles to track
        </label>
        <input
          id="bct-roles-description"
          data-testid="bct-roles-description"
          className={INPUT_CLASS}
          maxLength={500}
          placeholder="e.g. All C-level, VP-level and board members — or — CEO, CFO, COO, CTO, VP Sales, Board"
          value={form.rolesDescription}
          disabled={!importId}
          onChange={(e) => onChange({ rolesDescription: e.target.value })}
        />
        <p className="text-label text-text-secondary">
          Describe the roles or list them, separated by commas. AI sorts every job title at these
          companies into those roles; titles that match none are left out. Leave blank for:{" "}
          {DEFAULT_ROLES_HINT}.
        </p>
      </div>

      {/* Button row */}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button type="submit" data-testid="bct-submit" disabled={!canGenerate}>
          {generating ? <Spinner className="h-4 w-4" /> : null}
          Generate Tree
        </Button>
        <Button
          variant="secondary"
          type="button"
          data-testid="bct-csv-download"
          disabled={!hasResults}
          onClick={handleDownloadCsv}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="mr-1"
          >
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download CSV
        </Button>
        <span className="text-label text-text-secondary">
          Company Map output is CSV only — never written to Thrive.
        </span>
      </div>
    </form>
  );
}

function DegreeBadge({ degree }: { degree: string }) {
  const is1st = degree === "1st";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-label font-medium ${
        is1st ? "bg-accent-muted text-accent" : "bg-warning-muted text-warning-ink"
      }`}
    >
      {degree || "—"}
    </span>
  );
}

/** Exported for unit test: it is pure presentation over `rows`, and driving
 *  it through the whole page would need a preview round-trip to assert markup. */
export function BctResultsTable({
  rows,
  categories,
}: {
  rows: BctResultRow[];
  /** Display order, from the preview's classification. */
  categories: readonly string[];
}) {
  const groups = groupByCategory(rows, categories);
  if (groups.length === 0) return null;

  return (
    <div data-testid="bct-results-tables" className="flex flex-col gap-5">
      {groups.map(({ category, rows: categoryRows }) => (
        <div key={category} data-testid="bct-category-group">
          {/* Category header — accent color, uppercase (matches .bct-role-header) */}
          <div className="mb-2 pl-1 text-label font-bold uppercase tracking-wide text-accent">
            {category || "Uncategorized"} ({categoryRows.length})
          </div>
          <table className="w-full border-collapse text-label">
            <thead>
              <tr className="border-b border-border bg-surface-raised">
                {["Name", "Title", "Company", "At Company", "Degree"].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left text-label font-semibold text-text-secondary uppercase tracking-[.04em]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categoryRows.map((row, idx) => (
                <tr
                  key={`${category}-${idx}-${row["First Name"]}-${row["Last Name"]}`}
                  className="border-b last:border-0 transition-colors hover:bg-row-hover"
                >
                  <td className="px-3 py-2.5 font-medium text-text">
                    {/* Same treatment as the saved-import table
                        (`ContactsTable`), so the two surfaces behave alike.
                        Plain text without a URL — a dead href looks clickable
                        and goes nowhere. */}
                    {row["LinkedIn URL"] ? (
                      <a
                        href={row["LinkedIn URL"]}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:text-accent-hover hover:underline"
                      >
                        {row["First Name"]} {row["Last Name"]}
                      </a>
                    ) : (
                      <>
                        {row["First Name"]} {row["Last Name"]}
                      </>
                    )}
                  </td>
                  {/* Jared, 2026-09-30: "make sure that people see the title in
                      full in the output". Never truncated: every title that
                      placed the person, joined " / " by the API, wraps; a long
                      word breaks rather than overflowing (`break-words`, the
                      standard utility — Tailwind 3.4 has no `wrap-anywhere`). */}
                  <td
                    data-testid="bct-title-cell"
                    className="px-3 py-2.5 text-text-muted whitespace-normal break-words"
                  >
                    {row.Title || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-text">{row.Company || "—"}</td>
                  <td className="px-3 py-2.5 text-label text-text-muted">
                    {row.Start || "?"} – {row.End || "present"}
                  </td>
                  <td className="px-3 py-2.5">
                    <DegreeBadge degree={row.Degree} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

/** Exported so the cancellation test can drive the poll past its first tick. */
export const POLL_INTERVAL_MS = 2_500;
/** 20 minutes: the worst case of a saved run that fails on every receive
 *  (PR #982 review iter 3, A + C m-5). The queue gives it 3 receives with a
 *  450 s visibility timeout, so the last one starts at about 2 × 450 = 900 s
 *  and ends, inside the 180 s Lambda, by about 1,080 s (18 min); only then
 *  does the worker fail the run in plain words. At 10 minutes the page said
 *  "timed out" about runs still in their second or third try. */
export const POLL_TIMEOUT_MS = 1_200_000;

interface BctPageProps {
  client?: BctClient;
  importsClient?: ImportsClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
}

export function BctPage({
  client = DEFAULT_CLIENT,
  importsClient = DEFAULT_IMPORTS_CLIENT,
  meEmailOverride,
}: BctPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const me = useMe();
  // Drives the finder's "You" owner scope.
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");

  // The import comes from the app-wide selection; `<ImportSelectionBar>` owns
  // picking it, the `?importId=<id>` deep link included.
  const { selected } = useSelectedImport();
  const importId = selected?.importId ?? null;
  const importReady = selected !== null && bctUnusable(selected) === null;

  const [form, setForm] = useState<FormState>(makeEmptyForm);
  const [state, setState] = useState<PageState>({ phase: "idle" });
  const [error, setError] = useState<string | null>(null);

  /**
   * Generation token, NOT a boolean "is polling" flag.
   *
   * Every async handler captures `generationRef.current` at call time and
   * bails on each `setState` if it no longer matches. A boolean could only say
   * "stop", which the poll loop then could not distinguish from "the deadline
   * passed" — so a cancelled save fell through to the timeout branch and told
   * the recruiter the run had timed out when the server had actually accepted
   * it and was about to persist a child import. The token makes cancellation
   * and expiry different things.
   *
   * Bumped on: import change, unmount. Anything that invalidates in-flight
   * work for THIS import belongs here.
   */
  const generationRef = useRef(0);

  // Holds the selection bar's deferred `commit` while the "switching clears
  // companies" confirm dialog is open. Null when the dialog is closed.
  //
  // Pre-#625 this held a candidate import id and the page committed the switch
  // itself, because the page owned the `<SearchableSelect>`. The import is now
  // the app-wide selection, so the page vetoes and the BAR commits — which is
  // what keeps the URL deep-link param reconciled, and what lets this veto
  // compose with AI Review's "discard unapplied review?" on the same hook.
  const viewerAllowed = usePromptViewerAllowed();
  const [promptOpen, setPromptOpen] = useState(false);
  const [pendingImportSwitch, setPendingImportSwitch] = useState<{ commit: () => void } | null>(
    null
  );

  // A preview belongs to the import it was generated from — switching imports
  // (here, on AI Review, or via a deep link) must not leave the previous
  // import's rows on screen under a new heading. The companies go with it:
  // they were derived from the PREVIOUS import's positions, which is the same
  // reason the confirm dialog below exists. Doing it here rather than only in
  // the confirm covers every path into a switch, not just this page's own.
  const lastImportIdRef = useRef<string | null>(importId);
  useEffect(() => {
    const previous = lastImportIdRef.current;
    if (previous === importId) return;
    lastImportIdRef.current = importId;
    // Cancelling in-flight work and clearing the preview is safe on EVERY
    // transition, so it stays unconditional — nothing below depends on there
    // having been a previous import.
    generationRef.current += 1;
    setState({ phase: "idle" });
    setError(null);
    // The companies reset is the one thing that must NOT run on `null -> id`.
    // It exists to drop companies derived from the PREVIOUS import's positions,
    // and arriving at the first import has no previous import — but
    // `makeEmptyCompany()` mints a fresh `_key`, so running it here remounts
    // `<CompanyTypeahead>` instead of re-rendering it. That matters because
    // `?importId=` is resolved asynchronously by the selection bar now (a
    // `getImport` round-trip) rather than read off the URL at mount, so this
    // transition lands AFTER first paint and takes the typeahead's `focused`
    // state — and anything typed — with it.
    if (previous === null) return;
    setForm((prev) => ({ ...prev, companies: [makeEmptyCompany()] }));
  }, [importId]);

  useEffect(
    () => () => {
      generationRef.current += 1;
    },
    []
  );

  function patchForm(patch: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  /**
   * Veto hook for `<ImportSelectionBar>`. Switching people lists invalidates any
   * companies loaded from the previous import's positions, so confirm before
   * losing them; with nothing typed, switch freely.
   *
   * Returning `false` defers the commit to `confirmImportSwitch` below — the
   * bar hands us the callback rather than us calling `select()` directly, so
   * the deep-link param stays reconciled.
   */
  function allowImportSelect(_item: ImportSummary, commit: () => void): boolean {
    const hasLoadedCompany = form.companies.some((c) => c.name.trim() !== "");
    if (!hasLoadedCompany) return true;
    setPendingImportSwitch({ commit });
    return false;
  }

  function confirmImportSwitch() {
    // The companies reset rides along on the import-change effect above.
    pendingImportSwitch?.commit();
    setPendingImportSwitch(null);
  }

  const generating = state.phase === "previewing";
  const saving = state.phase === "saving" || state.phase === "polling";
  async function handleGenerate() {
    if (!importId) return;
    const generation = ++generationRef.current;
    setState({ phase: "previewing" });
    setError(null);
    const request = formToRequest(form);
    try {
      const response = await client.previewBctRun(importId, request);
      // The selection moved while this was in flight (here, on AI Review, or
      // via a deep link) — these rows belong to the previous import.
      if (generation !== generationRef.current) return;
      setState({ phase: "preview_ready", preview: { response, request } });
    } catch (err) {
      if (generation !== generationRef.current) return;
      setState({ phase: "idle" });
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    }
  }

  function goToImportsWithSavedNotice(run: BctRunResponse) {
    // Carry the reader's own `?preview=` tier across the redirect (F10): a
    // hardcoded `1` dropped a `?preview=2` reader back to the tier-1 page set.
    // Absent stays absent: a flag-on reader has no `?preview=` to preserve.
    const previewTier = searchParams.get("preview");
    const target =
      previewTier === null
        ? "/research/imports"
        : `/research/imports?preview=${encodeURIComponent(previewTier)}`;
    navigate(target, {
      state: {
        bctSaved: {
          matchCount: run.match_count,
          companiesLabel: companiesLabel(run.companies),
        },
      },
    });
  }

  async function handleSave(preview: PreviewResult) {
    if (!importId) return;
    const generation = ++generationRef.current;
    setState({ phase: "saving", preview });
    setError(null);

    // The save runs what the form says NOW. When that is still what the
    // preview answered, reference its classification (kept on the server) so
    // the saved run keeps the categories the recruiter just looked at;
    // otherwise the worker classifies the new request itself. Only the id
    // travels: the mapping put ~80 KB in this body for 1,567 titles, over the
    // WAF's ~8 KB limit (PR #982 review, A-1).
    const request = formToRequest(form);
    const previewId = preview.response.preview_classification_id;
    const body: BctRunRequest =
      previewId && sameRequest(request, preview.request)
        ? { ...request, preview_classification_id: previewId }
        : request;

    let pendingRun: BctRunResponse;
    try {
      pendingRun = await client.createBctRun(importId, body);
    } catch (err) {
      if (generation !== generationRef.current) return;
      setState({ phase: "preview_ready", preview });
      setError(err instanceof Error ? err.message : "Save failed. Please try again.");
      return;
    }
    // The run EXISTS server-side from here on. If the selection moved while the
    // POST was in flight, leave silently — reporting anything would attach the
    // old import's preview to the new import's screen.
    if (generation !== generationRef.current) return;

    if (pendingRun.status === "complete") {
      goToImportsWithSavedNotice(pendingRun);
      return;
    }

    setState({ phase: "polling", runId: pendingRun.id, preview });
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await new Promise<void>((r) => setTimeout(r, POLL_INTERVAL_MS));
      // Cancelled (the selection moved) — NOT expired. Returning here instead
      // of breaking is the whole point of the token: `break` would fall through
      // to the timeout branch below and report a failure for a run that is
      // alive and about to persist a child import.
      if (generation !== generationRef.current) return;
      try {
        const polled = await client.getBctRun(pendingRun.id);
        if (generation !== generationRef.current) return;
        if (polled.status === "complete") {
          goToImportsWithSavedNotice(polled);
          return;
        }
        if (polled.status === "failed") {
          setState({ phase: "preview_ready", preview });
          setError(polled.error ?? "Company Map run failed. Please try again.");
          return;
        }
      } catch {
        // transient network error — keep polling
      }
    }

    // Reached only by the deadline actually elapsing.
    setState({ phase: "preview_ready", preview });
    setError("Company Map run timed out after 20 minutes. Please check the People Lists page.");
  }

  const previewResult =
    state.phase === "preview_ready" || state.phase === "saving" || state.phase === "polling"
      ? state.preview
      : null;
  const preview = previewResult?.response ?? null;
  const previewId = preview?.preview_classification_id ?? null;

  return (
    <div className="flex flex-col gap-6 p-8">
      <PageHeading
        title="Company Map"
        description="Map references for a people list's candidates — past employees of the target company who held similar roles. Optional and independent: run it any time on a people list. Output is a downloadable CSV only, never written to Thrive."
        className="mb-6"
      />

      {/* Import selector — outside the content panel. Shared with AI Review:
          an import chosen there is already selected here. `bctUnusable` is the
          single rule about what this page can run on: it disables an unusable
          finder row in place (with the reason) and explains a shared selection
          that isn't usable here, instead of hiding either silently.

          `excludeTag="bct"` is the one deliberate exception to disable-not-hide:
          BCT-derived imports are dropped SERVER-SIDE (not client-side, so page
          and pager total still agree) because a BCT child can never seed
          another tree — product rule, 2026-08-12. They still reach this page
          via the shared selection (picked in AI Review) or a hand-written deep
          link, which is what the `bct` branch of `bctUnusable` catches.
          Under the Raw chip the picker adds `combined` to this exclusion
          itself (a combined list carries `raw` too), so the wire carries
          `exclude_tag=bct&exclude_tag=combined` there. */}
      <ImportSelectionBar
        client={importsClient}
        meEmail={meEmail}
        unusable={bctUnusable}
        excludeTag="bct"
        // Firm-wide by default, matching the dropdown this replaced
        // (`listImports({owner: "all"})`) — a recruiter who routinely runs BCT
        // on a colleague's import must not have it silently disappear.
        defaultOwner="all"
        onBeforeSelect={allowImportSelect}
      />

      {/* Content panel */}
      <div className="rounded-lg border border-border bg-surface-raised p-7">
        {/* Panel header: icon + title + subtitle */}
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-accent-muted">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-accent"
              aria-hidden="true"
            >
              <circle cx="12" cy="5" r="3" />
              <line x1="12" y1="8" x2="12" y2="14" />
              <circle cx="6" cy="19" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="12" y1="14" x2="6" y2="16" />
              <line x1="12" y1="14" x2="18" y2="16" />
            </svg>
          </div>
          <div>
            <h3 className="text-body font-bold text-text">Company Map</h3>
            <p className="mt-0.5 text-label text-text-secondary">
              Map connections between your network and the target company.
            </p>
          </div>
        </div>

        {/* Scope + role + buttons */}
        <BctForm
          form={form}
          onChange={patchForm}
          onGenerate={handleGenerate}
          generating={generating}
          preview={preview}
          searchCompanies={client.searchCompanies}
          importReady={importReady}
          saving={saving}
          // Gated on `importReady`, not on `importId` alone. A BLOCKED selection
          // (a BCT-derived import, or one still parsing) has a non-null id, so
          // passing it raw enabled the company typeahead — and fired
          // `GET /bct/companies?import_id=…` — against the very import the bar
          // is telling the recruiter this page cannot use. It also armed the
          // switch confirm, which would then offer to discard companies loaded
          // from a rejected import.
          importId={importReady ? importId : null}
        />

        {/* Error banner */}
        {error ? (
          <div
            role="alert"
            className="mt-5 rounded-sm border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
          >
            {error}
          </div>
        ) : null}

        {/* Results: one table per AI category */}
        {preview && preview.match_count > 0 ? (
          <div data-testid="bct-results" className="mt-6">
            <BctResultsTable rows={preview.rows} categories={preview.classification?.categories ?? []} />
          </div>
        ) : null}

        {/* Empty state */}
        {preview && preview.match_count === 0 ? (
          <div
            data-testid="bct-empty"
            className="mt-6 py-12 text-center text-body text-text-secondary"
          >
            {emptyMapMessage(preview)}
          </div>
        ) : null}

        {/* Info block + Save */}
        {preview ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-sm border border-border bg-surface-raised px-4 py-3">
            <span className="text-body text-text-secondary">
              <strong className="text-accent">{preview.match_count}</strong> contact
              {preview.match_count !== 1 ? "s" : ""} match the Company Map criteria. Preview only —
              not saved yet.
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {viewerAllowed && previewId ? (
                <Button
                  variant="secondary"
                  data-testid="bct-view-prompt"
                  onClick={() => setPromptOpen(true)}
                >
                  View prompt
                </Button>
              ) : null}
              <Button
                data-testid="bct-save"
                disabled={saving || preview.match_count === 0}
                onClick={() => previewResult && handleSave(previewResult)}
              >
                {saving ? <Spinner className="h-4 w-4" /> : null}
                Save
              </Button>
            </div>
          </div>
        ) : null}

        {promptOpen && previewId ? (
          <CompanyMapPromptModal
            client={client}
            source={{ kind: "preview", id: previewId }}
            onClose={() => setPromptOpen(false)}
          />
        ) : null}
      </div>

      <ConfirmDialog
        open={pendingImportSwitch !== null}
        title="Switch people list?"
        message="Switching people lists will clear the companies you've loaded — they were derived from the current one's positions. Continue?"
        confirmLabel="Switch and clear"
        destructive
        onConfirm={confirmImportSwitch}
        onCancel={() => setPendingImportSwitch(null)}
      />
    </div>
  );
}
